import * as stylex from '@stylexjs/stylex';
import { useForm } from '@tanstack/react-form';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useId } from 'react';
import { ToggleButtonGroup } from 'react-aria-components';
import { pipe as rPipe, map as rMap, filter as rFilter } from 'remeda';

import { OrganizationUserApiKeyStatusEnum } from '@/libs/openapi';
import {
  ToggleButton,
  View,
  Text,
  DialogBar,
  DIALOG_SLOTS,
  Button,
  Form,
} from '@/libs/serenity-ui';

interface FiltersFormProps {
  onClose?: () => void;
}

interface ApiKeyFiltersFormValues {
  status: number[];
}

export function ApiKeyFiltersForm({ onClose }: FiltersFormProps) {
  const id = useId();
  const search = useSearch({ from: '/_main/_list/api-keys/' });
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      status: [],
      ...Object.fromEntries(search.columnFilters.map(({ id: filterId, value }) => [ filterId, value ])),
    } as ApiKeyFiltersFormValues,
    onSubmit: () => {
      navigate({
        to: '.',
        search: (prev) => ({
          sorting: prev.sorting,
          columnFilters: rPipe(
            Object.entries(form.state.values),
            rFilter(([ , value ]) => value != null && !(Array.isArray(value) && value.length === 0)),
            rMap(([ filterId, value ]) => ({ id: filterId, value })),
          ),
          pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
        }),
      });
      onClose?.();
    },
  });

  return (
    <>
      <Form
        id={id}
        style={formStyles.base}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}>
        {(renderProps) => (
          <form.Field name="status">
            {(field) => (
              <View style={renderProps.fieldStyles}>
                <Text color={renderProps.labelColor} id={`${id}-status`} size={16}>Status</Text>
                <ToggleButtonGroup
                  aria-labelledby={`${id}-status`}
                  selectedKeys={new Set(field.state.value.map(String))}
                  selectionMode="multiple"
                  onSelectionChange={(keys) => field.handleChange([ ...keys ].map(Number))}
                  {...stylex.props(toggleButtonGroupStyles.base)}>
                  <ToggleButton id={String(OrganizationUserApiKeyStatusEnum.Value1)} size={32} variant="outlined">Active</ToggleButton>
                  <ToggleButton id={String(OrganizationUserApiKeyStatusEnum.Value0)} size={32} variant="outlined">Inactive</ToggleButton>
                </ToggleButtonGroup>
              </View>
            )}
          </form.Field>
        )}
      </Form>
      <DialogBar slot={DIALOG_SLOTS.BOTTOM_BAR}>
        <Button
          isRounded
          size={40}
          variant="ghost"
          onPress={() => {
            form.reset();
            navigate({
              to: '.',
              search: (prev) => ({
                sorting: prev.sorting,
                columnFilters: (prev.columnFilters ?? []).filter((f) => f.id === 'name'),
                pagination: { pageIndex: 0, pageSize: prev.pagination?.pageSize ?? 100 },
              }),
            });
          }}>
            Clear all
        </Button>
        <Button form={id} isRounded size={40} type="submit">Apply</Button>
      </DialogBar>
    </>
  );
}

const formStyles = stylex.create({
  base: {
    overflow: 'auto',
    paddingBlock: 8,
    scrollbarWidth: 'none',
  },
});

const toggleButtonGroupStyles = stylex.create({
  base: {
    alignContent: 'center',
    alignItems: 'center',
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 0,
    flexWrap: 'wrap',
    gap: 12,
  },
});
